import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useCreateCalendarExportLink,
  useRetrieveCalendarExportLinks,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { Clipeable } from "@common/clipeable";
import { Loader } from "@pages/calendarPage/sideBar/loader";
import { CopyIcon } from "@common/copyIcon";
import { Modal } from "@common/modal";
import { EyeIcon } from "@common/eyeIcon";
import { Props } from "./types";
import styles from "./styles.scss";
import { UseCases } from "@planzone/core";
import { usePlanzonePermissions } from "@components/permissions";

function CalendarExportLinks({ cancel }: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [isCopying, setIsCopying] = React.useState(false);
  const { isAdministrator } = usePlanzonePermissions();
  const { isRequestPending: isCreateRequestPending, createCalendarExportLink } =
    useCreateCalendarExportLink();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    links,
    retrieveCalendarExportLinks,
  } = useRetrieveCalendarExportLinks();
  const link = links[0];

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveCalendarExportLinks(account.resource);
  }, []);
  return (
    <Modal header={t("calendarExportLinks.title")} close={cancel}>
      <Safeguard
        useCases={[
          UseCases.createCalendarExportLink,
          UseCases.retrieveCalendarExportLinks,
        ]}
      >
        {({ canCreateCalendarExportLink, canRetrieveCalendarExportLinks }) =>
          (canCreateCalendarExportLink || canRetrieveCalendarExportLinks) && (
            <div className={styles.export}>
              {canRetrieveCalendarExportLinks && (
                <div className={styles.group}>
                  {isAdministrator && (
                    <div>
                      <div className={styles.exportLink}>
                        <div className={styles.container}>
                          <EyeIcon className={styles.icon} />
                          <p className={styles.title}>
                            {t("calendarExportLinks.meOnly")}
                          </p>
                        </div>
                        <p className={styles.link}>
                          {window.location.origin}
                          /api/ical/
                          {link.uri}/admin
                        </p>
                        <Clipeable
                          value={`${window.origin}/api/ical/${link.uri}/admin`}
                          clipEnd={() => setIsCopying(false)}
                        >
                          {(clipStart) => (
                            <div
                              className={styles.copyButton}
                              onClick={() => {
                                setIsCopying(true);
                                clipStart();
                              }}
                            >
                              {isCopying ? (
                                <div className={styles.copyIcon}>
                                  <Loader />
                                </div>
                              ) : (
                                <CopyIcon className={styles.copyIcon} />
                              )}
                            </div>
                          )}
                        </Clipeable>
                      </div>
                    </div>
                  )}
                  <div className={styles.exportLink}>
                    <div className={styles.container}>
                      <EyeIcon className={styles.icon} />
                      <p className={styles.title}>
                        {isAdministrator
                          ? t("calendarExportLinks.everyone")
                          : t("calendarExportLinks.meOnly")}
                      </p>
                    </div>
                    <p className={styles.link}>
                      {window.location.origin}
                      /api/ical/
                      {link.uri}
                    </p>
                    <Clipeable
                      value={`${window.origin}/api/ical/${link.uri}`}
                      clipEnd={() => setIsCopying(false)}
                    >
                      {(clipStart) => (
                        <div
                          className={styles.copyButton}
                          onClick={() => {
                            setIsCopying(true);
                            clipStart();
                          }}
                        >
                          {isCopying ? (
                            <div className={styles.copyIcon}>
                              <Loader />
                            </div>
                          ) : (
                            <CopyIcon className={styles.copyIcon} />
                          )}
                        </div>
                      )}
                    </Clipeable>
                  </div>
                </div>
              )}
            </div>
          )
        }
      </Safeguard>
    </Modal>
  );
}

export { CalendarExportLinks };
